import { useState } from 'react';
import axios from 'axios';
import { Flag, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { id: 'wrong_photos', label: "Wrong photos / not what's pictured" },
  { id: 'copyright', label: "I own these photos / unauthorised use" },
  { id: 'sold_or_unavailable', label: 'No longer available / already sold' },
  { id: 'misleading_or_scam', label: 'Misleading description / suspected scam' },
  { id: 'duplicate', label: 'Duplicate of another listing' },
  { id: 'off_topic', label: 'Not a building material / off topic' },
  { id: 'other', label: 'Other' },
];

/**
 * Discreet "Report listing" link + dialog used on the listing detail page.
 *
 * Anyone — signed in or not — can submit; we capture an optional email so
 * we can follow up. Backend rate-limits anonymous reports by IP.
 */
export default function ReportListingButton({ listingId, user }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('wrong_photos');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/listings/${listingId}/report`, {
        reason,
        message: message.trim() || null,
        reporter_email: email.trim() || null,
      });
      toast.success('Report submitted — thank you. We review every report within 48 hours.');
      setOpen(false);
      setMessage('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit your report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-muted-foreground/70 hover:text-amber-300 inline-flex items-center gap-1.5 transition-colors"
        data-testid={`report-listing-btn-${listingId}`}
      >
        <Flag className="w-3 h-3" />
        Report listing
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="report-listing-dialog">
          <DialogHeader>
            <DialogTitle>Report this listing</DialogTitle>
            <DialogDescription>
              Spotted an issue with this listing? Tell us what's wrong and our team will review
              within 48 hours. {user ? '' : 'You can report anonymously, or leave an email if you\'d like a reply.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Reason
              </Label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full h-10 mt-1 rounded-md border border-border/30 bg-background/40 px-3 text-sm"
                data-testid="report-reason-select"
              >
                {REASONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>

            <div>
              <Label htmlFor="r-msg" className="text-xs uppercase tracking-wider text-muted-foreground">
                More detail (optional)
              </Label>
              <Textarea
                id="r-msg"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={reason === 'copyright' ? 'Where was the original photo taken from? What URL?' : 'Anything else we should know?'}
                className="text-sm bg-background/40 mt-1"
                data-testid="report-message-input"
                maxLength={2000}
              />
            </div>

            {!user && (
              <div>
                <Label htmlFor="r-email" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Your email (optional)
                </Label>
                <Input
                  id="r-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-background/40 mt-1"
                  data-testid="report-email-input"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={submitting} data-testid="report-submit-btn" className="btn-gold">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Flag className="w-4 h-4 mr-2" />}
              Submit report
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
